import { Fragment, useState } from "react";
import { motion } from "framer-motion";
import { Check, CornerDownRight, Circle, Minus, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import { FunctionalUnitMap, fmCurrent } from "@/components/FunctionalUnitMap";
import PartOrganization from "@/components/PartOrganization";

/*
  VISUAL PARAGRAPH MAP — PARAGRAPH FUNCTION MAP (grain: INSIDE one paragraph).
    ROW 1 = the five canonical communicative functions (Opening / Thesis / Elaboration / Evidence-Example /
            Conclusion) as a vertical ladder, each with ONE grounded sentence saying what that function is doing
            in THIS student's paragraph, plus its state:
              ✓ done · ● current (YOU ARE HERE) · ○ to come · — not needed · ↳ carried by another function.
            A branch (state E) forks INSIDE the current function: this paragraph / maybe a new paragraph.
    ROW 2 = the Reader Model: what the reader understands now, and the single meaning they still need.
  Purely presentational; reads the deterministic visual_map payload (backend pp_visual_map). No LLM, no fetch.
  Restored from the 2026-08-11 tower (ElementRow / Arrow / Mark) in the 2026-09 Representation Recovery Pass 1.
*/

const TERRA = "#8C3A2A";

// STATUS BADGE (display-only; derived from the authoritative visual_map element.status).
// Never implies absence for an element that exists — prefer DEVELOPING over "not developed".
const STATUS_META = {
  done: { label: "STRONG", cls: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
  carried: { label: "PRESENT", cls: "bg-stone-200 text-stone-600 ring-stone-300" },
  current: { label: "DEVELOPING", cls: "bg-white text-[#8C3A2A] ring-white/70" },
  not_needed: { label: "NOT NEEDED HERE", cls: "bg-stone-50 text-stone-400 ring-stone-200" },
};
function statusMeta(el) {
  const s = (el.status || "").toLowerCase();
  if (STATUS_META[s]) return STATUS_META[s];
  // to_come / unknown: DEVELOPING when there is real content, else a neutral not-yet (genuinely absent).
  if (el.detail && !/^(not yet|to come|absent|missing|none|implied$)/i.test((el.detail || "").trim()))
    return { label: "DEVELOPING", cls: "bg-amber-50 text-amber-800 ring-amber-200" };
  return { label: "NOT YET", cls: "bg-stone-50 text-stone-400 ring-stone-200" };
}

const tidOf = (key) => (key || "").replace(/\W+/g, "-").toLowerCase();
// THREE FUNCTIONAL DIAGRAM FAMILIES (the paragraph's role in the essay) — the movement inside each is whatever TWS read in THIS paragraph
const FAMILY = { introduction: "Introduction · what this paragraph is doing", body: "Body paragraph · what this paragraph is doing", conclusion: "Conclusion · what this paragraph is doing" };
const NODE_STATUS = { completed: "done", current: "current", remaining: "to_come" };
const MOVE_STATUS = { done: "IN PLACE", to_come: "NOT YET" };

/* The five states, five distinct glyphs. */
function Mark({ status }) {
  const base = "flex h-5 w-5 shrink-0 items-center justify-center";
  if (status === "done")
    return (
      <span className={`${base} rounded-full bg-emerald-100 text-emerald-700`} data-testid="map-mark-done" aria-label="accomplished">
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
    );
  if (status === "current")
    return (
      <span className={`${base} rounded-full bg-white`} style={{ color: TERRA }} data-testid="map-mark-current" aria-label="working on this now">
        <span className="h-2.5 w-2.5 rounded-full bg-current" />
      </span>
    );
  if (status === "carried")
    return (
      <span className={`${base} text-stone-400`} data-testid="map-mark-carried" aria-label="carried by another part">
        <CornerDownRight className="h-3.5 w-3.5" strokeWidth={2.5} />
      </span>
    );
  if (status === "not_needed")
    return (
      <span className={`${base} text-stone-300`} data-testid="map-mark-not-needed" aria-label="not needed here">
        <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
      </span>
    );
  return (
    <span className={`${base} text-stone-400`} data-testid="map-mark-to-come" aria-label="may still be needed">
      <Circle className="h-3 w-3" strokeWidth={2} />
    </span>
  );
}

function Branch({ branch }) {
  return (
    <div className="mt-3 space-y-1.5 border-t border-white/25 pt-2.5" data-testid="map-branch">
      <div className="flex items-start gap-2">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
        <p className="text-[12px] text-white/90" data-testid="map-branch-current">
          <span className="font-semibold">This paragraph:</span> {branch.current_lane}
        </p>
      </div>
      <div className="flex items-start gap-2">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full border border-white/70" />
        <p className="text-[12px] text-white/90" data-testid="map-branch-emerging">
          <span className="font-semibold">Maybe a new paragraph:</span> {branch.emerging_lane}
        </p>
      </div>
      {branch.decision && (
        <p className="pl-3.5 text-[11.5px] italic text-white/80" data-testid="map-branch-decision">{branch.decision}</p>
      )}
    </div>
  );
}

function ElementRow({ el, isCurrent, branch }) {
  const s = el.status;
  const isThesis = el.key === "THESIS";
  const tid = tidOf(el.key);
  const quiet = !isThesis && (s === "not_needed" || s === "carried");

  // QUIET states (carried / not needed): one muted line — the function is accounted for, not absent.
  if (quiet) {
    return (
      <div className="flex min-w-0 items-center gap-2.5 px-3 py-1" data-node-state={s} data-testid={`map-el-${tid}`}>
        <Mark status={s} />
        <p className="shrink-0 text-[11px] font-bold uppercase tracking-[0.09em] text-stone-400">{el.label}</p>
        {el.detail && (
          <p className="min-w-0 truncate text-[12px] italic text-stone-400" data-testid={`map-el-detail-${tid}`}>{el.detail}</p>
        )}
      </div>
    );
  }

  const current = s === "current";
  const style = current ? { background: TERRA, borderColor: TERRA } : {};
  const boxCls = current ? "text-white shadow-md" : s === "done" ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-stone-200";
  const labelCls = current ? "text-white" : s === "done" || isThesis ? "text-stone-800" : "text-stone-600";
  const detailCls = current ? "text-white/95" : isThesis ? "text-stone-700" : "text-stone-600";

  return (
    <div
      className={`rounded-xl border px-4 ${current ? "py-3" : "py-2.5"} ${boxCls}`}
      style={style}
      data-node-state={s}
      data-testid={`map-el-${tid}`}
      aria-current={isCurrent ? "step" : undefined}
    >
      <div className="flex items-start gap-3">
        <Mark status={s} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <p className={`text-[12.5px] font-bold uppercase tracking-[0.09em] ${labelCls}`}>{el.label}</p>
            {isCurrent ? (
              <span
                data-testid="map-you-are-here"
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.18em] shadow-sm"
                style={{ color: TERRA }}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
                You are here
              </span>
            ) : (() => {
              const meta = statusMeta(el);
              return (
                <span
                  data-testid={`map-el-status-${tid}`}
                  className={`inline-flex items-center rounded-full px-2 py-[1px] text-[9px] font-bold uppercase tracking-[0.12em] ring-1 ${meta.cls}`}
                >
                  {meta.label}
                </span>
              );
            })()}
          </div>
          {el.detail && (
            <p className={`mt-1 text-[13px] leading-snug ${detailCls}`} data-testid={`map-el-detail-${tid}`}>{el.detail}</p>
          )}
          {el.status_note && (
            <p className={`mt-0.5 flex items-center gap-1 text-[11px] italic ${current ? "text-white/70" : "text-stone-400"}`} data-testid="el-status-note">
              <CornerDownRight className="h-3 w-3" strokeWidth={2} /> {el.status_note}
            </p>
          )}
          {el.action && current && (
            <p className="mt-1.5 text-[12px] leading-snug text-white/85" data-testid="el-action">{el.action}</p>
          )}
        </div>
      </div>

      {/* Branch (state E): a fork shown from the relevant function, not a separate map. */}
      {branch && <Branch branch={branch} />}
    </div>
  );
}

/* ACTIVE RELATIONSHIP — shown IN THE DIAGRAM, on the connector between the two functions it relates
   (e.g. Thesis → Elaboration). One compact line: the naive-reader question that relation must answer. */
const REL_TO_KEY = { Thesis: "THESIS", Elaboration: "ELABORATION", Evidence: "EVIDENCE / EXAMPLE", Conclusion: "CONCLUSION" };
export const relTargetKey = (rel, elements, currentKey) => {
  const k = rel ? REL_TO_KEY[rel.to] : null;
  if (k && elements.some((e) => e.key === k)) return k;
  return currentKey || null;
};

function RelArrow({ rel }) {
  return (
    <div className="flex items-stretch gap-2 py-0.5 pl-[3px]" data-testid="map-active-relationship" data-rel-key={rel.key}>
      <div className="flex w-3 shrink-0 flex-col items-center" aria-hidden>
        <span className="w-[2px] flex-1 rounded" style={{ backgroundColor: TERRA }} />
        <ArrowRight className="h-3 w-3 rotate-90" style={{ color: TERRA }} strokeWidth={3} />
      </div>
      <p className="min-w-0 self-center text-[11.5px] leading-snug text-stone-600">
        <span className="font-semibold uppercase tracking-[0.12em]" style={{ color: TERRA }} data-testid="map-active-relationship-path">
          {rel.from} → {rel.to}
        </span>
        {rel.label && <span data-testid="map-active-relationship-label"> · {rel.label}</span>}
      </p>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex justify-center py-px" aria-hidden>
      <span className="text-[13px] leading-none text-stone-400">↓</span>
    </div>
  );
}

/* Collapsed view of the functional map — keeps the one thing the student must not lose: what we are working on. */
function FmSummary({ fm }) {
  const cur = fmCurrent(fm);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="map-summary">
      <div className="flex items-center gap-1" aria-hidden>
        {fm.movements.map((m, i) => <Mark key={i} status={NODE_STATUS_FM[m.state] || "to_come"} />)}
      </div>
      {cur && (
        <p className="min-w-0 text-[12.5px] text-stone-700" data-testid="map-summary-current">
          <span className="font-bold uppercase tracking-[0.12em]" style={{ color: TERRA }}>You are here</span>
          <span className="text-stone-500"> — {cur.content}</span>
        </p>
      )}
    </div>
  );
}
const NODE_STATUS_FM = { done: "done", current: "current", open: "to_come" };

/* Collapsed view — never loses the one thing the student must not lose: where they are. */
function MapSummary({ elements, current }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="map-summary">
      <div className="flex items-center gap-1" aria-hidden>
        {elements.map((el) => <Mark key={el.key} status={el.status} />)}
      </div>
      {current && (
        <p className="min-w-0 text-[12.5px] text-stone-700" data-testid="map-summary-current">
          <span className="font-bold uppercase tracking-[0.12em]" style={{ color: TERRA }}>You are here · {current.label}</span>
          {current.detail && <span className="text-stone-500"> — {current.detail}</span>}
        </p>
      )}
    </div>
  );
}

/* READER MODEL — what the reader ACTUALLY understands from the current draft, and the single most
   important meaning still missing FOR THE READER. HYBRID: a concise combined sentence when the
   established relations form one clean proposition, else check-lines. Never restates assignment demands. */
function ReaderModel({ rm }) {
  const mode = rm.understands_mode;
  const sentence = (rm.understands_sentence || "").trim();
  const lines = Array.isArray(rm.understands) ? rm.understands.filter(Boolean) : [];
  const hasUnderstands = (mode === "sentence" && !!sentence) || lines.length > 0;
  const stillNeeds = (rm.still_needs || "").trim();
  return (
    <div data-testid="reader-gap-card" className="space-y-3">
      {/* PRIMARY, prominent reader information: the single meaning the reader still needs. */}
      {stillNeeds ? (
        <div className="rounded-xl border border-[#8C3A2A]/25 bg-[#fbf6f2] p-4" data-testid="reader-needs">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: TERRA }}>What the reader still needs</p>
          <div className="mt-2 flex items-start gap-2">
            <span className="mt-[-1px] text-[16px] font-bold leading-none" style={{ color: TERRA }} aria-hidden>?</span>
            <p className="text-[13.5px] leading-relaxed text-stone-800" data-testid="reader-needs-detail">{stillNeeds}</p>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="reader-needs">
          <p className="text-[13.5px] leading-relaxed text-stone-700" data-testid="reader-needs-complete">
            The reader can now follow your whole point — read it back and see if the connections feel clear.
          </p>
        </div>
      )}

      {/* SECONDARY, collapsed by default: what the reader already understands (preserved, quiet). */}
      {hasUnderstands ? (
        <details className="group" data-testid="reader-understands-disclosure">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700 select-none">
            <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2.5} />
            What the reader already understands
            <span className="text-stone-400 transition-transform group-open:rotate-90" aria-hidden>&rsaquo;</span>
          </summary>
          <div data-testid="reader-understands" className="mt-2 pl-5">
            {mode === "sentence" && sentence ? (
              <p className="text-[13.5px] leading-snug text-stone-700" data-testid="reader-understands-sentence">{sentence}</p>
            ) : (
              <div className="space-y-1.5">
                {lines.map((t, i) => (
                  <div key={i} className="flex items-start gap-2" data-testid={`reader-understands-line-${i}`}>
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" strokeWidth={2.5} />
                    <p className="text-[13.5px] leading-snug text-stone-700">{t}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      ) : (
        <p className="text-[12.5px] italic leading-snug text-stone-500" data-testid="reader-understands-empty">
          The reader hasn’t learned your main point yet — start by stating it.
        </p>
      )}
    </div>
  );
}

export default function ParagraphMap({ map, operation, focusNote, isMulti = false, compact = false }) {
  const [open, setOpen] = useState(true);
  if (!map || !map.map_state) return null;
  // HOW THIS PART IS PUT TOGETHER — when the reading supplies the unit's organization of meaning, THAT is the representation (compact and
  // full alike): the abstract movement ladder is replaced, not doubled. A persistent writing tool, shown regardless of support rung.
  const organization = map.functional_map && map.functional_map.organization && Array.isArray(map.functional_map.organization.rows) ? map.functional_map.organization : null;
  if (organization) {
    return (
      <div data-testid="paragraph-map" data-map-state={map.map_state} data-compact={compact ? "1" : undefined} data-diagram="organization"
        className="rounded-xl border border-stone-200 bg-white px-3 py-2">
        <PartOrganization org={organization} />
      </div>
    );
  }
  const elements = map.elements || [];
  const take = map.take || {};
  const currentKey = map.current_element;
  const currentEl = elements.find((e) => e.status === "current") || elements.find((e) => e.key === currentKey) || null;
  // INCREMENT 3 — ONE reading, several representations: whenever the backend's functional-unit reading has movements
  // (`functional_map`), BOTH the compact and the full map project IT. The five slots / role nodes below are the
  // legacy / degraded fallback only (no functional reading available).
  const fm = map.functional_map && Array.isArray(map.functional_map.movements) && map.functional_map.movements.length ? map.functional_map : null;
  // CANONICAL DIAGRAM RULE (decided deterministically on the backend — `functional_map.show_diagram`): a diagram is shown only when the
  // current object is a coherent canonical unit (introduction / body / conclusion) whose reading gives a meaningful organization to see.
  // When a functional reading exists and says "no diagram", NOTHING is drawn — never the five-slot ladder to fill the space.
  if (map.functional_map && map.functional_map.show_diagram === false) return null;
  const fmBranch = fm && map.branch && map.map_state === "E" && (map.branch.current_lane || map.branch.emerging_lane) ? map.branch : null;
  if (compact && fm) {
    return (
      <div data-testid="paragraph-map" data-map-state={map.map_state} data-compact="1" data-family={map.family || ""} data-diagram="functional"
        className="rounded-xl border border-stone-200 bg-white px-3 py-2">
        <FunctionalUnitMap fm={fm} compact />
      </div>
    );
  }
  // COMPACT (the focused paragraph workspace): the map SHOWS — what THIS paragraph is doing within what KIND of essay part it is.
  // The DCO's role-named movement (`nodes`, read through the essay role's canonical domain: introduction / body / conclusion) is
  // the diagram; the generic five slots (`elements`) stand in ONLY for a body paragraph whose reading carries no movement yet.
  // No grounded sentences, no action, no branch, no reader model, no focus issue: the conversation explains and teaches.
  if (compact) {
    const family = FAMILY[map.family] || null;
    const movement = Array.isArray(map.nodes) ? map.nodes.filter((n) => n && (n.role || n.move)) : [];
    const useMovement = movement.length > 0;
    const genericOk = !map.family || map.family === "body";                       // an introduction / conclusion never gets the body template
    const rows = useMovement
      ? movement.map((n) => ({ key: n.role || n.move, label: (n.role || n.move).toUpperCase(), status: NODE_STATUS[n.state] || "to_come" }))
      : (genericOk ? elements.map((el) => ({ key: el.key, label: el.label, status: el.status, el })) : []);
    const curKey = useMovement ? (movement.find((n) => n.state === "current") || {}).role : (currentEl && currentEl.key);
    return (
      <div data-testid="paragraph-map" data-map-state={map.map_state} data-compact="1" data-family={map.family || ""} data-diagram={useMovement ? "movement" : (rows.length ? "generic" : "none")}
        className="rounded-xl border border-stone-200 bg-white px-3 py-2">
        {family ? <p data-testid="map-family" className="mb-1 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-400">{family}</p> : null}
        {rows.length === 0 ? (
          <p data-testid="map-movement-empty" className="text-[12px] italic text-stone-400 px-1 py-1">TWS has not yet read the functions of this paragraph.</p>
        ) : (
        <ol data-testid="map-structure" className="space-y-0.5">
          {rows.map((r, i) => {
            const cur = useMovement ? (r.key === curKey && (movement[i] || {}).state === "current") : (currentEl ? r.key === currentEl.key : false);
            const quiet = !cur && (r.status === "not_needed" || r.status === "carried");
            return (
              <Fragment key={`${r.key}-${i}`}>
                {i > 0 ? <Arrow /> : null}
                <li data-testid={`map-el-${tidOf(r.key)}`} data-node-state={r.status} aria-current={cur ? "step" : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-3 py-1.5 ${cur ? "text-white shadow-sm" : quiet ? "text-stone-400" : "text-stone-700"}`}
                  style={cur ? { background: TERRA } : {}}>
                  <Mark status={r.status} />
                  <span className="text-[11.5px] font-bold uppercase tracking-[0.09em]">{r.label}</span>
                  {cur ? (
                    <span data-testid="map-you-are-here" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-[2px] text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: TERRA }}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> You are here
                    </span>
                  ) : (
                    <span data-testid={`map-el-status-${tidOf(r.key)}`} className="ml-auto text-[9px] font-bold uppercase tracking-[0.12em] text-stone-400">
                      {useMovement ? (MOVE_STATUS[r.status] || "") : statusMeta(r.el).label}
                    </span>
                  )}
                </li>
              </Fragment>
            );
          })}
        </ol>
        )}
      </div>
    );
  }
  const branch = map.branch && map.map_state === "E" && (map.branch.current_lane || map.branch.emerging_lane) ? map.branch : null;
  const branchKey = branch ? (branch.element || currentEl?.key || "ELABORATION") : null;
  const isStructuralSelection = (operation || "").toLowerCase() === "structural_selection";
  const unit = isMulti || (fm && (fm.unit || {}).paragraphs > 1) ? "this part" : "your paragraph";
  const rels = Array.isArray(map.relationships) ? map.relationships : [];
  const activeRel = rels.find((r) => r && r.key === map.active_relationship) || rels.find((r) => r && r.active) || null;
  const relKey = activeRel ? relTargetKey(activeRel, elements, currentEl?.key || currentKey) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="overflow-hidden rounded-xl border border-stone-200 bg-white"
      data-testid="paragraph-map"
      data-map-state={map.map_state}
      data-map-open={open ? "1" : "0"}
    >
      <div className="p-4">
        {/* ROW 1 — the five canonical functions as a vertical ladder, top → bottom (reader order). */}
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">How {unit} is put together</p>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="paragraph-map-toggle"
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-stone-200 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500 transition-colors hover:border-[#8C3A2A] hover:text-[#8C3A2A]"
          >
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {open ? "Hide map" : "Show map"}
          </button>
        </div>
        {isStructuralSelection && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid="map-focus-issue">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800">Focus issue</p>
            <p className="mt-0.5 text-[12.5px] leading-snug text-amber-900" data-testid="map-focus-issue-message">
              Several strong ideas are competing for space in this {isMulti ? "part" : "paragraph"}.
            </p>
            {focusNote && (
              <p className="mt-1 text-[12px] leading-snug text-amber-800/90" data-testid="map-focus-issue-detail">{focusNote}</p>
            )}
          </div>
        )}
        {open ? (
          fm ? (
            <FunctionalUnitMap fm={fm} branch={fmBranch ? <Branch branch={fmBranch} /> : null} />
          ) : (
          <div data-testid="map-structure">
            {elements.map((el, i) => {
              const relHere = relKey && el.key === relKey ? activeRel : null;
              return (
                <Fragment key={el.key}>
                  {relHere ? <RelArrow rel={relHere} /> : (i > 0 && <Arrow />)}
                  <ElementRow
                    el={el}
                    isCurrent={el.status === "current" || (!!currentKey && el.key === currentKey)}
                    branch={branchKey && el.key === branchKey ? branch : null}
                  />
                </Fragment>
              );
            })}
          </div>
          )
        ) : fm ? (
          <FmSummary fm={fm} />
        ) : (
          <MapSummary elements={elements} current={currentEl} />
        )}

        {/* ROW 2 — reader model, OPTIONAL: collapsed by default so the diagram stays the one place to look. */}
        <details className="mt-3 group" data-testid="paragraph-map-reading">
          <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400 transition-colors hover:text-[#8C3A2A]" data-testid="paragraph-map-reading-toggle">
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
            What the reader understands
          </summary>
          <div className="mt-3" data-testid="paragraph-map-reading-body">
          {map.reader_model ? <ReaderModel rm={map.reader_model} /> : (() => {
            const lines = Array.isArray(take.coverage_lines) ? take.coverage_lines : [];
            const understood = lines.filter((l) => l && l.ok);
            const gaps = lines.filter((l) => l && !l.ok);
            const gap = gaps[0] || null; // the SINGLE communicative gap being worked on

            // Fallback when the assignment has no parsed demands (single-demand task): show the
            // learner's established point + the one guiding move, still content-first (no structural labels).
            if (lines.length === 0) {
              const rels = Array.isArray(map.relationships) ? map.relationships : [];
              const active = rels.find((r) => r.active) || rels[0] || null;
              const placeholder = (t) => !t || /^(not yet developed|still taking shape|present but incomplete|implied|not needed)/i.test(t);
              const established = take.thesis && !placeholder(take.thesis) ? take.thesis : "";
              const nextMove = (active && active.move) ? active.move : (take.next || take.right_now || "");
              return (
                <div data-testid="reader-gap-card" className="space-y-3">
                  {nextMove && (
                    <div className="rounded-xl border border-[#8C3A2A]/25 bg-[#fbf6f2] p-4" data-testid="reader-needs">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: TERRA }}>What the reader still needs</p>
                      <div className="mt-1.5 flex items-start gap-2">
                        <span className="mt-[-1px] text-[15px] font-bold" style={{ color: TERRA }} aria-hidden>?</span>
                        <p className="text-[14px] leading-relaxed text-stone-800">{nextMove}</p>
                      </div>
                    </div>
                  )}
                  {established && (
                    <details className="group" data-testid="reader-understands-disclosure">
                      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700 select-none">
                        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2.5} />
                        What the reader already understands
                        <span className="text-stone-400 transition-transform group-open:rotate-90" aria-hidden>&rsaquo;</span>
                      </summary>
                      <p data-testid="reader-understands" className="mt-2 pl-5 text-[13.5px] leading-snug text-stone-700">{established}</p>
                    </details>
                  )}
                  {!established && !nextMove && (
                    <p className="text-[14px] italic leading-snug text-stone-500">Start by stating your main point.</p>
                  )}
                </div>
              );
            }

            return (
              <div data-testid="reader-gap-card" className="space-y-3">
                {gap ? (
                  <div className="rounded-xl border border-[#8C3A2A]/25 bg-[#fbf6f2] p-4" data-testid="reader-needs">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: TERRA }}>What the reader still needs</p>
                    <div className="mt-2 flex items-start gap-2">
                      <span className="mt-[-1px] text-[16px] font-bold leading-none" style={{ color: TERRA }} aria-hidden>?</span>
                      <div>
                        {gap.label && (
                          <p className="text-[14px] font-medium leading-snug text-stone-900" data-testid="reader-needs-label">{gap.label}</p>
                        )}
                        <p className="mt-1 text-[13.5px] leading-relaxed text-stone-700" data-testid="reader-needs-detail">{gap.text}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="reader-needs">
                    <p className="text-[13.5px] leading-relaxed text-stone-700">
                      {isMulti ? "This part" : "Your paragraph"} now answers what the question asks — read it back and strengthen how the ideas connect.
                    </p>
                  </div>
                )}

                {understood.length > 0 && (
                  <details className="group" data-testid="reader-understands-disclosure">
                    <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700 select-none">
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2.5} />
                      What the reader already understands
                      <span className="text-stone-400 transition-transform group-open:rotate-90" aria-hidden>&rsaquo;</span>
                    </summary>
                    <div data-testid="reader-understands" className="mt-2 pl-5 space-y-1.5">
                      {understood.map((l, i) => (
                        <div key={i} className="flex items-start gap-2" data-testid={`reader-understands-line-${i}`}>
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" strokeWidth={2.5} />
                          <p className="text-[13.5px] leading-snug text-stone-700">{l.label || l.text}</p>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                <details className="mt-3 border-t border-stone-100 pt-3" data-testid="about-feedback">
                  <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-400 hover:text-stone-600 select-none">
                    About this feedback
                  </summary>
                  <p className="mt-2 text-[12px] leading-relaxed text-stone-500">
                    The Writing Development Studio is designed primarily for expository, explanatory, and
                    argumentative writing. Narrative examples and personal experiences may be used as
                    evidence, but some style suggestions reflect expository-writing conventions and should
                    be treated as coaching judgments rather than mandatory corrections.
                  </p>
                </details>
              </div>
            );
          })()}
          </div>
        </details>

      </div>
    </motion.div>
  );
}
